import React, { useState } from 'react';
import { Search, Clock, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { VideoPlayerScreen } from './VideoPlayerScreen';
interface WorkoutsScreenProps {
  navigateTo: (screen: string) => void;
}
interface Workout {
  id: string;
  title: string;
  description: string;
  duration: string;
  level: string;
  type: string;
  focus: string;
  energy: string;
  thumbnail: string;
  icon: React.ReactNode;
  videoUrl: string;
}
export const WorkoutsScreen = ({
  navigateTo
}: WorkoutsScreenProps) => {
  const [energyFilter, setEnergyFilter] = useState('All Energy');
  const [focusFilter, setFocusFilter] = useState('All Focus');
  const [typeFilter, setTypeFilter] = useState('All');
  const [durationFilter, setDurationFilter] = useState('All');
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | null>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  // Sample workout data
  const workouts: Workout[] = [{
    id: '1',
    title: 'Quick Burn',
    description: 'High-intensity interval training to torch calories fast',
    duration: '15',
    level: 'Intermediate',
    type: 'HIIT',
    focus: 'Cardio',
    energy: 'High',
    thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    icon: <span className="text-yellow-500" data-id="element-1694">⚡</span>,
    videoUrl: 'https://www.youtube.com/watch?v=oAPCPjnU1wA'
  }, {
    id: '2',
    title: 'Full Body Blast',
    description: 'Complete strength training targeting all major muscle groups',
    duration: '30',
    level: 'Advanced',
    type: 'Strength',
    focus: 'Strength',
    energy: 'High',
    thumbnail: 'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    icon: <span className="text-amber-500" data-id="element-1695">💪</span>,
    videoUrl: 'https://www.youtube.com/watch?v=DHOPWvO3ZcI'
  }, {
    id: '3',
    title: 'Morning Flow',
    description: 'Gentle yoga sequence to start your day with mindful movement',
    duration: '20',
    level: 'Beginner',
    type: 'Yoga',
    focus: 'Flexibility',
    energy: 'Low',
    thumbnail: 'https://images.unsplash.com/photo-1575052814086-f385e2e2ad1b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    icon: <span className="text-green-500" data-id="element-1696">🌿</span>,
    videoUrl: 'https://www.youtube.com/watch?v=j7rKKpwdXNE'
  }, {
    id: '4',
    title: 'Cardio Sprint',
    description: 'Fast-paced cardio to boost your heart rate and energy',
    duration: '10',
    level: 'Intermediate',
    type: 'Cardio',
    focus: 'Cardio',
    energy: 'High',
    thumbnail: 'https://images.unsplash.com/photo-1599058917765-a780eda07a3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1469&q=80',
    icon: <span className="text-red-500" data-id="element-1697">❤️</span>,
    videoUrl: 'https://www.youtube.com/watch?v=ml6cT4AZdqI'
  }, {
    id: '5',
    title: 'Core Power',
    description: 'Targeted core strengthening for stability and power',
    duration: '15',
    level: 'Intermediate',
    type: 'Strength',
    focus: 'Core',
    energy: 'Medium',
    thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    icon: <span className="text-green-600" data-id="element-1698">🔄</span>,
    videoUrl: 'https://www.youtube.com/watch?v=Xyd_fa5zoEU'
  }];
  // Filter workouts based on selected filters
  const filteredWorkouts = workouts.filter(workout => {
    const matchesEnergy = energyFilter === 'All Energy' || workout.energy.toLowerCase() === energyFilter.toLowerCase();
    const matchesFocus = focusFilter === 'All Focus' || workout.focus.toLowerCase() === focusFilter.toLowerCase();
    const matchesType = typeFilter === 'All' || workout.type.toLowerCase() === typeFilter.toLowerCase();
    const matchesDuration = durationFilter === 'All' || workout.duration === durationFilter;
    return matchesEnergy && matchesFocus && matchesType && matchesDuration;
  });
  const handleWorkoutClick = (workout: Workout) => {
    setSelectedWorkout(workout);
    setShowVideoPlayer(true);
  };
  const handleVideoNavigate = (screen: string) => {
    setShowVideoPlayer(false);
    navigateTo(screen);
  };
  const TypeFilterButton = ({
    type,
    active
  }) => <button className={`rounded-full px-6 py-2 text-sm font-medium transition-colors ${active ? type === 'All' ? 'bg-green-600 text-white' : type === 'Strength' ? 'bg-gray-700 text-white' : type === 'Cardio' ? 'bg-red-500 text-white' : type === 'Yoga' ? 'bg-green-500 text-white' : type === 'HIIT' ? 'bg-blue-500 text-white' : 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setTypeFilter(type)} data-id="element-1699">
      {type}
    </button>;
  const DurationFilterButton = ({
    duration,
    active
  }) => <button className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setDurationFilter(duration)} data-id="element-1700">
      {duration === 'All' ? 'All' : `${duration} min`}
    </button>;
  // If video player is showing, render the VideoPlayerScreen component
  if (showVideoPlayer && selectedWorkout) {
    return <VideoPlayerScreen navigateTo={handleVideoNavigate} workout={selectedWorkout} data-id="element-1701" />;
  }
  return <div className="flex flex-col h-full min-h-screen bg-white pb-20" data-id="element-1702">
      <div className="p-6" data-id="element-1703">
        <div className="flex justify-between items-center mb-6" data-id="element-1704">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1705">Workouts</h1>
          <button className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100" data-id="element-1706">
            <Search className="w-5 h-5 text-gray-600" data-id="element-1707" />
          </button>
        </div>
        {/* Filters */}
        <div className="space-y-5 mb-6" data-id="element-1708">
          <div className="grid grid-cols-2 gap-4" data-id="element-1709">
            <div data-id="element-1710">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1711">
                Energy Level
              </label>
              <div className="relative" data-id="element-1712">
                <select value={energyFilter} onChange={e => setEnergyFilter(e.target.value)} className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white pr-10" data-id="element-1713">
                  <option data-id="element-1714">All Energy</option>
                  <option data-id="element-1715">Low</option>
                  <option data-id="element-1716">Medium</option>
                  <option data-id="element-1717">High</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-1718">
                  <ChevronDown className="w-4 h-4" data-id="element-1719" />
                </div>
              </div>
            </div>
            <div data-id="element-1720">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1721">
                Focus Area
              </label>
              <div className="relative" data-id="element-1722">
                <select value={focusFilter} onChange={e => setFocusFilter(e.target.value)} className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white pr-10" data-id="element-1723">
                  <option data-id="element-1724">All Focus</option>
                  <option data-id="element-1725">Cardio</option>
                  <option data-id="element-1726">Strength</option>
                  <option data-id="element-1727">Flexibility</option>
                  <option data-id="element-1728">Core</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-1729">
                  <ChevronDown className="w-4 h-4" data-id="element-1730" />
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-1731">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1732">
              Workout Type
            </label>
            <div className="flex space-x-2 overflow-x-auto pb-2 -mx-1 px-1" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-1733">
              <style jsx data-id="element-1734">{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              <TypeFilterButton type="All" active={typeFilter === 'All'} data-id="element-1735" />
              <TypeFilterButton type="Strength" active={typeFilter === 'Strength'} data-id="element-1736" />
              <TypeFilterButton type="Cardio" active={typeFilter === 'Cardio'} data-id="element-1737" />
              <TypeFilterButton type="Yoga" active={typeFilter === 'Yoga'} data-id="element-1738" />
              <TypeFilterButton type="HIIT" active={typeFilter === 'HIIT'} data-id="element-1739" />
            </div>
          </div>
        </div>
        {/* Duration filters */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2 -mx-1 px-1" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-1740">
          <style jsx data-id="element-1741">{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          <DurationFilterButton duration="All" active={durationFilter === 'All'} data-id="element-1742" />
          <DurationFilterButton duration="5" active={durationFilter === '5'} data-id="element-1743" />
          <DurationFilterButton duration="10" active={durationFilter === '10'} data-id="element-1744" />
          <DurationFilterButton duration="15" active={durationFilter === '15'} data-id="element-1745" />
          <DurationFilterButton duration="20" active={durationFilter === '20'} data-id="element-1746" />
          <DurationFilterButton duration="30" active={durationFilter === '30'} data-id="element-1747" />
          <DurationFilterButton duration="45" active={durationFilter === '45'} data-id="element-1748" />
          <DurationFilterButton duration="60" active={durationFilter === '60'} data-id="element-1749" />
        </div>
        {/* Workout list */}
        <div className="space-y-4" data-id="element-1750">
          {filteredWorkouts.map(workout => <div key={workout.id} className="rounded-xl overflow-hidden border border-gray-100 shadow-sm" onClick={() => handleWorkoutClick(workout)} data-id="element-1751">
              <div className="relative" data-id="element-1752">
                <img src={workout.thumbnail} alt={workout.title} className="w-full h-48 object-cover" data-id="element-1753" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1754">
                  <div className="w-16 h-16 bg-white bg-opacity-80 rounded-full flex items-center justify-center" data-id="element-1755">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500" data-id="element-1756">
                      <polygon points="5 3 19 12 5 21 5 3" data-id="element-1757"></polygon>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-4" data-id="element-1758">
                <div className="flex justify-between items-start mb-1" data-id="element-1759">
                  <h3 className="text-lg font-bold" data-id="element-1760">{workout.title}</h3>
                  <div className={`px-4 py-1 rounded-full text-white text-sm ${workout.type === 'Cardio' ? 'bg-red-500' : workout.type === 'Strength' ? 'bg-green-700' : workout.type === 'Yoga' ? 'bg-green-500' : workout.type === 'HIIT' ? 'bg-blue-500' : 'bg-blue-500'}`} data-id="element-1761">
                    {workout.type}
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3" data-id="element-1762">
                  {workout.description}
                </p>
                <div className="flex items-center" data-id="element-1763">
                  <div className="flex items-center mr-3" data-id="element-1764">
                    <Clock className="w-4 h-4 text-gray-500 mr-1" data-id="element-1765" />
                    <span className="text-sm text-gray-500" data-id="element-1766">
                      {workout.duration} min
                    </span>
                  </div>
                  <div className="bg-gray-100 rounded-full px-3 py-1 text-sm" data-id="element-1767">
                    {workout.level}
                  </div>
                  <div className="ml-auto text-2xl" data-id="element-1768">{workout.icon}</div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};